import { useState } from 'react';
import { Box, Flex, HStack, Spinner, Text, Tr, useToast } from '@chakra-ui/react';
import { Button, ConfirmDialog, Icon, Notice, StatusBadge, TokenChip } from '../../../../ui';
import {
    SETTINGS_TABLE_GUTTER,
    SettingsPageActions,
    SettingsRowActions,
    SettingsTable,
    SettingsTd,
    SettingsTh,
} from '../../../../components/settings/primitives';
import EmployeeAccessModal from './EmployeeAccessModal';
import InviteHandoffModal, { inviteToastOptions, needsHandoff, type InviteHandoff } from './InviteHandoffModal';
import {
    inviteLinkFor,
    teamErrorMessage,
    useInviteEmployee,
    useRevokeEmployeeInvite,
    useYaxleTeam,
    type EmployeeAccess,
    type YaxleTeamInvite,
    type YaxleTeamMember,
} from './useYaxleTeam';

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const accessSummary = (access: EmployeeAccess): string => {
    if (access === 'admin') return 'Admin';
    if (access.permissions.length === 0 && access.tabs.length === 0) return 'No access';
    return `${plural(access.tabs.length, 'tab')}, ${plural(access.permissions.length, 'permission')}`;
};

const formatDate = (iso: string | null) =>
    iso
        ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '—';

/**
 * Settings → Yaxle → Team: every employee account, with the access it carries
 * into each org, plus pending invites. Only team managers reach it.
 */
const YaxleTeamSection = () => {
    const toast = useToast();
    const { data, isLoading, isError, error } = useYaxleTeam();
    const resendInvite = useInviteEmployee();
    const revokeInvite = useRevokeEmployeeInvite();
    const [isInviting, setIsInviting] = useState(false);
    // Kept after close so the modal doesn't flip to the invite form while it animates out.
    const [editing, setEditing] = useState<YaxleTeamMember | null>(null);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [revoking, setRevoking] = useState<YaxleTeamInvite | null>(null);
    const [resendingId, setResendingId] = useState<string | null>(null);
    const [resendHandoff, setResendHandoff] = useState<InviteHandoff | null>(null);

    if (isLoading) {
        return (
            <Flex justify="center" py={16}>
                <Spinner />
            </Flex>
        );
    }
    if (isError || !data) {
        return (
            <Box px={SETTINGS_TABLE_GUTTER}>
                <Notice tone="error" title="Couldn't load the Yaxle team">
                    {teamErrorMessage(error, 'Try again in a moment.')}
                </Notice>
            </Box>
        );
    }

    const handleResend = (invite: YaxleTeamInvite) => {
        setResendingId(invite.id);
        resendInvite.mutate(
            {
                email: invite.email,
                ...(invite.firstName ? { firstName: invite.firstName } : {}),
                ...(invite.lastName ? { lastName: invite.lastName } : {}),
                access: invite.access,
                canManageTeam: invite.canManageTeam,
            },
            {
                onSuccess: (result) => {
                    if (needsHandoff(result)) {
                        setResendHandoff({ email: invite.email, name: invite.firstName ?? invite.email, result });
                    } else {
                        toast(inviteToastOptions(invite.email, result));
                    }
                },
                onError: (resendError) =>
                    toast({
                        status: 'error',
                        title: "Couldn't resend the invite",
                        description: teamErrorMessage(resendError, 'Try again in a moment.'),
                        duration: 8000,
                        isClosable: true,
                    }),
                onSettled: () => setResendingId(null),
            },
        );
    };

    const openEditor = (member: YaxleTeamMember) => {
        setEditing(member);
        setIsEditOpen(true);
    };

    // For when the email doesn't arrive (e.g. a mailbox Microsoft 365 is still setting up).
    const handleCopyLink = (invite: YaxleTeamInvite) => {
        navigator.clipboard
            .writeText(inviteLinkFor(invite.id))
            .then(
                () => toast({ status: 'success', title: `Copied ${invite.email}'s invite link`, isClosable: true }),
                () => toast({ status: 'error', title: "Couldn't copy the link", isClosable: true }),
            );
    };

    const handleRevoke = () => {
        if (!revoking) return;
        revokeInvite.mutate(revoking.id, {
            onSuccess: () => {
                toast({ status: 'success', title: `Revoked the invite for ${revoking.email}`, isClosable: true });
                setRevoking(null);
            },
            onError: (revokeError) =>
                toast({
                    status: 'error',
                    title: "Couldn't revoke the invite",
                    description: teamErrorMessage(revokeError, 'Try again in a moment.'),
                    isClosable: true,
                }),
        });
    };

    return (
        <>
            <SettingsPageActions>
                <Button size="sm" leftIcon={<Icon name="plus" size="sm" />} onClick={() => setIsInviting(true)}>
                    Invite employee
                </Button>
            </SettingsPageActions>

            {data.microsoft365Provisioning && (
                <Box px={SETTINGS_TABLE_GUTTER} pb={4} flexShrink={0}>
                    <Notice tone="info">
                        Inviting also sets up the person’s Microsoft 365 account: it creates one if they don’t have it
                        yet, gives it the employee license, and adds it to the employee group.
                    </Notice>
                </Box>
            )}

            <SettingsTable
                head={
                    <>
                        <SettingsTh>Name</SettingsTh>
                        <SettingsTh>Email</SettingsTh>
                        <SettingsTh>Access</SettingsTh>
                        <SettingsTh>Team manager</SettingsTh>
                        <SettingsTh>Status</SettingsTh>
                        <SettingsTh>Last active</SettingsTh>
                        <SettingsTh w="1%" />
                    </>
                }
            >
                {data.members.map((member) => (
                    <Tr
                        key={member.id}
                        cursor="pointer"
                        _hover={{ bg: 'bg.subtle' }}
                        onClick={() => openEditor(member)}
                    >
                        <SettingsTd fontWeight="medium" color="fg.default" whiteSpace="nowrap">
                            <HStack spacing={2}>
                                <Text as="span">{`${member.firstName} ${member.lastName}`.trim()}</Text>
                                {member.isYou && <TokenChip>You</TokenChip>}
                            </HStack>
                        </SettingsTd>
                        <SettingsTd>{member.email}</SettingsTd>
                        <SettingsTd>{accessSummary(member.access)}</SettingsTd>
                        <SettingsTd>{member.canManageTeam ? 'Yes' : '—'}</SettingsTd>
                        <SettingsTd>
                            <StatusBadge tone={member.active ? 'success' : 'neutral'}>
                                {member.active ? 'Active' : 'Deactivated'}
                            </StatusBadge>
                        </SettingsTd>
                        <SettingsTd whiteSpace="nowrap">{formatDate(member.lastActive)}</SettingsTd>
                        <SettingsTd onClick={(event) => event.stopPropagation()}>
                            <SettingsRowActions onEdit={() => openEditor(member)} editLabel="Edit access" />
                        </SettingsTd>
                    </Tr>
                ))}
                {data.invites.map((invite) => (
                    <Tr key={invite.id}>
                        <SettingsTd fontWeight="medium" color="fg.default" whiteSpace="nowrap">
                            {`${invite.firstName ?? ''} ${invite.lastName ?? ''}`.trim() || '—'}
                        </SettingsTd>
                        <SettingsTd>{invite.email}</SettingsTd>
                        <SettingsTd>{accessSummary(invite.access)}</SettingsTd>
                        <SettingsTd>{invite.canManageTeam ? 'Yes' : '—'}</SettingsTd>
                        <SettingsTd>
                            <StatusBadge tone="info">Invited</StatusBadge>
                        </SettingsTd>
                        <SettingsTd whiteSpace="nowrap">{`Sent ${formatDate(invite.sentAt)}`}</SettingsTd>
                        <SettingsTd>
                            <HStack spacing={1} justify="flex-end">
                                <Button size="xs" variant="ghostGray" onClick={() => handleCopyLink(invite)}>
                                    Copy link
                                </Button>
                                <Button
                                    size="xs"
                                    variant="ghostGray"
                                    onClick={() => handleResend(invite)}
                                    isLoading={resendingId === invite.id}
                                >
                                    Resend
                                </Button>
                                <SettingsRowActions onDelete={() => setRevoking(invite)} deleteLabel="Revoke invite" />
                            </HStack>
                        </SettingsTd>
                    </Tr>
                ))}
            </SettingsTable>

            <EmployeeAccessModal
                isOpen={isInviting}
                onClose={() => setIsInviting(false)}
                microsoft365Provisioning={data.microsoft365Provisioning}
            />
            {editing && (
                <EmployeeAccessModal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} member={editing} />
            )}
            <InviteHandoffModal handoff={resendHandoff} onClose={() => setResendHandoff(null)} />
            <ConfirmDialog
                isOpen={revoking !== null}
                onClose={() => setRevoking(null)}
                onConfirm={handleRevoke}
                title="Revoke this invite?"
                confirmLabel="Revoke"
                confirmColorScheme="red"
                isConfirmLoading={revokeInvite.isPending}
            >
                {revoking?.email} won’t be able to use the link in their email. Their Microsoft 365 license and
                group membership stay as they are.
            </ConfirmDialog>
        </>
    );
};

export default YaxleTeamSection;
